import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Formik } from 'formik';

import InputContainer from '@RHEnvironmental/pattern-library/dist/js/components/InputContainer';
import InlineButtonPanel from '@RHEnvironmental/pattern-library/dist/js/components/InlineButtonPanel';
import Button from '@RHEnvironmental/pattern-library/dist/js/components/Button';
import PageHeading from '@RHEnvironmental/pattern-library/dist/js/components/PageHeading';
import LoadingIndicator from '@RHEnvironmental/pattern-library/dist/js/components/LoadingIndicator';
import InputField from '@RHEnvironmental/pattern-library/dist/js/components/InputField';
import RadioButton from '@RHEnvironmental/pattern-library/dist/js/components/RadioButton';
import TextAreaField from '@RHEnvironmental/pattern-library/dist/js/components/TextAreaField';
import AlertBox from '@RHEnvironmental/pattern-library/dist/js/components/AlertBox';
import BodyText from '../../modules/BodyText';
import SpreadOfHarmAccordion from './SpreadOfHarmAccordion';

import { HARM_CLASSES } from '../../../constants/HHSRS';
import * as editScoringActions from '../../../actions/editScoring';
import {
    calculateMostLikelyRating,
    getLikelihoodScaleDescription,
    getMostLikelyHarmFieldName,
    isValidCalculation,
} from '../../../services/hhsrs/HHSRSCalculator';
import { scrollToPos } from '../../../services/utils';
import { RenderIf } from '../../modules/RenderIf/RenderIf';
import { Box, Radio } from '@chakra-ui/react';

const EditScoring = ({
    onSuccess,
    inspection,
    getNationalAverages = undefined,
    isNewDesign = false,
    form = undefined,
    hazard,
    nationalAverages = undefined,
    toggledClass = undefined,
    refreshCalculatedScore = undefined,
    calculatedScore = undefined,
    toggleClass = undefined,
    isLoading = undefined,
    isRemovingWarningFlag = undefined,
    onSubmit = undefined,
    onClose,
    likelihoodScalePoints = undefined,
    reset = undefined,
    clearRescoreWarning = undefined,
    jumpToHazardsLocation,
    getHHSRSScoring,
    viewOnly = false
}) => {
    useEffect(() => {
        getNationalAverages(inspection, hazard);

        return () => {
            reset();
        };
    }, []);

    const isClassToggled = (className) => className === toggledClass;

    const maxWidth = isNewDesign ? undefined : '1024px';
    const className = isNewDesign ? undefined : 'edit-scoring';
    const mb = isNewDesign ? 8 : undefined;

    const title = viewOnly ? `Scoring - ${hazard.name}` : `Edit Scoring - ${hazard.name}`;

    return (
        <Box mb={mb} maxWidth={maxWidth} className={className}>
            {isLoading ? (
                <LoadingIndicator show className="mt-2" />
            ) : (
                <>
                    {hazard.scoring?.requires_rescore && !viewOnly && (
                        <>
                            <AlertBox className="mb-2" theme="warning">
                                <p>
                                    It is advisable that you review your hazard
                                    scoring assessment in the event of altering
                                    either the dwelling type, dwelling age or
                                    hazard related defects.
                                </p>
                            </AlertBox>
                            <LoadingIndicator show={isRemovingWarningFlag} />
                            <Button
                                className="mb-6"
                                onClick={async () => {
                                    await clearRescoreWarning(hazard, inspection);
                                    getHHSRSScoring?.();
                                }}
                            >
                                Clear rescore warning
                            </Button>
                        </>
                    )}
                    <Formik
                        onSubmit={(formValues, formHelpers) =>
                            onSubmit(
                                formValues,
                                formHelpers,
                                inspection,
                                onSuccess
                            )
                        }
                        enableReinitialize
                        initialValues={form.initialValues}
                        initialStatus={form.initialStatus}
                    >
                        {({
                            values,
                            errors,
                            handleChange,
                            handleSubmit,
                            setFieldValue,
                            isSubmitting,
                        }) => {
                            // eslint-disable-next-line react-hooks/rules-of-hooks
                            useEffect(() => {
                                // Refresh the score whenever a form value is updated.
                                refreshCalculatedScore(values);

                                if (values.most_likely_rating === '') {
                                    setFieldValue(
                                        'most_likely_rating',
                                        getMostLikelyHarmFieldName(values)
                                    );
                                }

                                // Refresh the most likely outcome whenever a form value is updated.
                                setFieldValue(
                                    values.most_likely_rating,
                                    calculateMostLikelyRating(
                                        values,
                                        values.most_likely_rating
                                    )
                                );
                            }, [values]);

                            const currentLikelihoodIndex =
                                likelihoodScalePoints.indexOf(
                                    values.likelihood
                                );

                            const isLowerAdjusterDisabled =
                                currentLikelihoodIndex ===
                                likelihoodScalePoints.length - 1;
                            const isUpperAdjusterDisabled =
                                currentLikelihoodIndex === 0;

                            return (
                                <>
                                    <RenderIf condition={!!jumpToHazardsLocation}>
                                        <Button
                                            className="jump-button"
                                            isInverted
                                            role="button"
                                            onClick={() =>
                                                scrollToPos(
                                                    jumpToHazardsLocation.offsetTop,
                                                    jumpToHazardsLocation.offsetLeft,
                                                    72
                                                )
                                            }
                                        >
                                            Survey findings
                                        </Button>
                                    </RenderIf>
                                    <form>
                                        <PageHeading
                                            level={4}
                                            className="mb-4 edit-scoring__title"
                                        >
                                            {title}
                                        </PageHeading>
                                        <InputContainer className="mb-4">

                                            <RenderIf condition={!viewOnly}>
                                            <PageHeading
                                                level={4}
                                                className="mb-2"
                                            >
                                                Likelihood
                                            </PageHeading>
                                                <div className="likelihood-selector mb-4">
                                                    <div className="likelihood-selector__numerator">
                                                        <span>1</span>
                                                    </div>
                                                    <div className="likelihood-selector__denominator">
                                                        <Button
                                                            disabled={
                                                                isLowerAdjusterDisabled
                                                            }
                                                            onClick={() => {
                                                                setFieldValue(
                                                                    'likelihood',
                                                                    likelihoodScalePoints[
                                                                        currentLikelihoodIndex +
                                                                            1
                                                                    ]
                                                                );
                                                            }}
                                                        >
                                                            -
                                                        </Button>
                                                        <div>
                                                            <InputField
                                                                id="likelihood"
                                                                onChange={
                                                                    handleChange
                                                                }
                                                                value={
                                                                    values.likelihood
                                                                }
                                                                aria-required="true"
                                                                name="likelihood"
                                                                disabled
                                                                type="text"
                                                            />
                                                        </div>
                                                        <Button
                                                            disabled={
                                                                isUpperAdjusterDisabled
                                                            }
                                                            onClick={() => {
                                                                setFieldValue(
                                                                    'likelihood',
                                                                    likelihoodScalePoints[
                                                                        currentLikelihoodIndex -
                                                                            1
                                                                    ]
                                                                );
                                                            }}
                                                        >
                                                            +
                                                        </Button>
                                                    </div>
                                                    <BodyText className="likelihood-selector--nav">
                                                        National Average (
                                                        {nationalAverages.type}):{' '}
                                                        {
                                                            nationalAverages.likelihood
                                                        }
                                                    </BodyText>

                                                    <div className="likelihood-description">
                                                        <span>
                                                            {getLikelihoodScaleDescription(
                                                                likelihoodScalePoints,
                                                                values.likelihood,
                                                                nationalAverages.likelihood
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                            </RenderIf>
                                            <PageHeading
                                                level={4}
                                                className="mb-2"
                                            >
                                                Likelihood Justification*
                                            </PageHeading>
                                            <TextAreaField
                                                name="likelihood_justification"
                                                id="likelihood-justification"
                                                required
                                                className="justification-textarea"
                                                onChange={handleChange}
                                                disabled={viewOnly}
                                                value={
                                                    values.likelihood_justification
                                                }
                                                errors={
                                                    errors &&
                                                    errors.likelihood_justification
                                                }
                                            />

                                            <RenderIf condition={!viewOnly}>
                                                <PageHeading
                                                    level={4}
                                                    className="mb-2"
                                                >
                                                    Spread of Harms
                                                </PageHeading>
                                                <BodyText>
                                                    Select the most likely outcome
                                                    with the radio button. This will
                                                    be set to 100% minus the
                                                    percentage sum of all other
                                                    outcomes.
                                                </BodyText>
                                                {HARM_CLASSES.map((harmClass) => (
                                                    <div
                                                        className="harm-accordion__container"
                                                        key={harmClass.fieldName}
                                                    >
                                                        <SpreadOfHarmAccordion
                                                            isToggled={isClassToggled(
                                                                harmClass.fieldName
                                                            )}
                                                            onToggle={() => {
                                                                const isMostLikelyClass =
                                                                    values.most_likely_rating ===
                                                                    harmClass.fieldName;

                                                                const classToToggle =
                                                                    harmClass.fieldName ===
                                                                        toggledClass ||
                                                                    isMostLikelyClass
                                                                        ? null
                                                                        : harmClass.fieldName;
                                                                toggleClass(
                                                                    classToToggle
                                                                );
                                                            }}
                                                            name={harmClass.name}
                                                            nationalAverage={
                                                                nationalAverages[
                                                                    harmClass
                                                                        .fieldName
                                                                ]
                                                            }
                                                            setRating={(rating) => {
                                                                setFieldValue(
                                                                    harmClass.fieldName,
                                                                    rating
                                                                );
                                                            }}
                                                            rating={
                                                                values[
                                                                    harmClass
                                                                        .fieldName
                                                                ]
                                                            }
                                                        />
                                                        <Radio
                                                            ml="14px"
                                                            mt="12px"
                                                            alignSelf="flex-start"
                                                            name="most_likely_rating"
                                                            value={harmClass.fieldName}
                                                            isChecked={values.most_likely_rating === harmClass.fieldName}
                                                            onChange={(e) => {
                                                                setFieldValue('most_likely_rating', e.target.value);
                                                            }}
                                                        >
                                                            {harmClass.label}
                                                        </Radio>
                                                    </div>
                                                ))}
                                            </RenderIf>
                                        </InputContainer>
                                        {!isValidCalculation(values) && (
                                            <BodyText>
                                                The spread of harm outcomes must
                                                add up to 100%
                                            </BodyText>
                                        )}
                                        <PageHeading level={4} className="mb-2">
                                            Spread of Harms Justification*
                                        </PageHeading>
                                        <TextAreaField
                                            name="harms_justification"
                                            id="harms-justification"
                                            required
                                            disabled={viewOnly}
                                            className="justification-textarea"
                                            onChange={handleChange}
                                            value={values.harms_justification}
                                            errors={
                                                errors &&
                                                errors.harms_justification
                                            }
                                        />
                                        <PageHeading level={4} className="mb-2">
                                            Score
                                        </PageHeading>
                                        <BodyText>
                                            {calculatedScore?.score.toFixed(1)}
                                        </BodyText>
                                        <PageHeading level={4} className="mb-2">
                                            Rating
                                        </PageHeading>
                                        <div className="band">
                                            <span>
                                                Band {calculatedScore?.band}
                                            </span>
                                        </div>
                                        <InlineButtonPanel>
                                            <Button
                                                className="button--white"
                                                onClick={onClose}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                onClick={handleSubmit}
                                                disabled={
                                                    isSubmitting ||
                                                    !isValidCalculation(values) ||
                                                    viewOnly
                                                }
                                            >
                                                Save
                                            </Button>
                                        </InlineButtonPanel>
                                    </form>
                                </>
                            );
                        }}
                    </Formik>
                </>
            )}
        </Box>
    );
};

const mapStateToProps = (state) => ({
    ...state.pages.editScoring,
});

const mapDispatchToProps = (dispatch) => ({
    getNationalAverages(inspection, hazard) {
        dispatch(editScoringActions.getNationalAverages(inspection, hazard));
    },
    refreshCalculatedScore(formValues) {
        dispatch(editScoringActions.refreshCalculatedScore(formValues));
    },
    toggleClass(harmClass) {
        dispatch(editScoringActions.harmClassToggled(harmClass));
    },
    reset() {
        dispatch(editScoringActions.reset());
    },
    clearRescoreWarning(hazard, inspection) {
        dispatch(editScoringActions.clearRescoreWarning(hazard, inspection));
    },
    onSubmit(formValues, formHelpers, inspection, onSuccess) {
        dispatch(
            editScoringActions.onSubmit(
                formValues,
                formHelpers,
                inspection,
                onSuccess
            )
        );
    },
});
export default connect(mapStateToProps, mapDispatchToProps)(EditScoring);
